import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Calendar, CreditCard, X } from 'lucide-react';
import { axiosInstance } from '../App';

/**
 * FoundersRenewalReminder — Iter 119 (Feb 2026) value-focused banner shown to
 * Founding Businesses approaching the end of their free 12-month window.
 *
 * Stages (kicks in at the earliest matching window):
 *   30 days remaining → soft heads-up (yellow)
 *   14 days remaining → stronger reminder (amber)
 *    7 days remaining → urgent (orange)
 *
 * Wording is deliberately VALUE-focused ("keep your events, offers, followers,
 * direct customer notifications active") and NOT payment-focused. The CTA is
 * "Save payment method" (opens the existing SetupIntent hosted checkout) or
 * "Continue Full Access" if a card is already saved.
 *
 * Dismissable per stage — once dismissed, the banner won't re-appear during
 * THAT stage but will return at the next stage threshold.
 */
const STORAGE_PREFIX = 'foundersRenewalDismissed-';

const FoundersRenewalReminder = () => {
  const { t } = useTranslation();
  const [state, setState] = useState(null); // {plan, freeUntil, daysLeft, hasCard}
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const [meRes, cardRes] = await Promise.all([
          axiosInstance.get('/business/me'),
          axiosInstance.get('/stripe/saved-card').catch(() => ({ data: { hasCard: false } })),
        ]);
        if (cancelled) return;
        const b = meRes.data || {};
        const freeUntil = b.foundersFreeUntil || b.subscriptionEnd;
        if (b.subscriptionPlan !== 'founders' || !freeUntil) { setState(null); return; }
        const daysLeft = Math.ceil((new Date(freeUntil).getTime() - Date.now()) / 86400000);
        setState({ freeUntil, daysLeft, hasCard: !!cardRes.data?.hasCard });
      } catch {
        setState(null);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  if (!state) return null;
  const { daysLeft, hasCard } = state;

  // Pick the matching stage. Outside any window → don't render.
  let stage = null;
  if (daysLeft <= 7 && daysLeft >= 0) stage = '7';
  else if (daysLeft <= 14 && daysLeft > 7) stage = '14';
  else if (daysLeft <= 30 && daysLeft > 14) stage = '30';
  if (!stage) return null;

  const storageKey = `${STORAGE_PREFIX}${stage}`;
  const dismissed = (() => { try { return localStorage.getItem(storageKey) === '1'; } catch { return false; } })();
  if (dismissed) return null;

  const tone = {
    '30': { bg: 'bg-yellow-50', br: 'border-yellow-300', tx: 'text-yellow-900', sub: 'text-yellow-800/80', btn: 'bg-yellow-600 hover:bg-yellow-700' },
    '14': { bg: 'bg-amber-50', br: 'border-amber-300', tx: 'text-amber-900', sub: 'text-amber-800/80', btn: 'bg-amber-600 hover:bg-amber-700' },
    '7':  { bg: 'bg-orange-50', br: 'border-orange-300', tx: 'text-orange-900', sub: 'text-orange-800/80', btn: 'bg-orange-600 hover:bg-orange-700' },
  }[stage];

  const dismiss = () => {
    try { localStorage.setItem(storageKey, '1'); } catch { /* ignore */ }
    setState({ ...state, _hidden: true });
    // Force re-render via state change so the dismissed banner unmounts
    setState(s => ({ ...s }));
  };

  const handleCta = async () => {
    setBusy(true);
    try {
      // Same SetupIntent hosted-checkout path as SavedCardBanner.
      const res = await axiosInstance.post('/stripe/create-setup-checkout', {
        originUrl: window.location.origin,
      });
      if (res.data?.checkoutUrl) window.location.assign(res.data.checkoutUrl);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[renewal-reminder] setup checkout failed', e);
      setBusy(false);
    }
  };

  const ctaLabel = hasCard
    ? t('renewal.ctaContinue', 'Continue Full Access')
    : t('renewal.ctaSaveCard', 'Save payment method');

  return (
    <div
      className={`flex items-start gap-3 px-4 py-3 ${tone.bg} border ${tone.br} rounded-xl text-sm relative`}
      data-testid={`founders-renewal-reminder-${stage}d`}
    >
      <Calendar className={`h-5 w-5 ${tone.tx} flex-shrink-0 mt-0.5`} />
      <div className="flex-1 pr-7">
        <p className={`font-semibold ${tone.tx}`}>
          {t(`renewal.title.${stage}d`, {
            '30': 'Your Founding year ends in 30 days',
            '14': 'Your Founding year ends in 2 weeks',
            '7':  'Your Founding year ends in 1 week',
          }[stage])}
        </p>
        <p className={`text-xs leading-snug mt-0.5 ${tone.sub}`}>
          {t('renewal.body', 'Keep your events, offers, followers and direct customer notifications active without interruption.')}
        </p>
      </div>
      <button
        type="button"
        onClick={handleCta}
        disabled={busy}
        className={`px-3 py-1.5 ${tone.btn} text-white text-xs font-semibold rounded-lg disabled:opacity-60 inline-flex items-center gap-1.5`}
        data-testid="founders-renewal-cta"
      >
        <CreditCard className="h-3.5 w-3.5" />
        {busy ? t('renewal.opening', 'Opening…') : ctaLabel}
      </button>
      <button
        type="button"
        onClick={dismiss}
        className={`absolute top-2 right-2 p-1 ${tone.tx} opacity-60 hover:opacity-100`}
        data-testid="founders-renewal-dismiss"
        aria-label={t('renewal.dismiss', 'Dismiss')}
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
};

export default FoundersRenewalReminder;
